import React, { FC, useState } from 'react';
import { DatePicker, notification } from 'antd';
import { Dayjs } from 'dayjs';
import Checkbox from 'components/Elements/Checkbox';
import './hourlyRateModal.scss';

export interface ExportParams {
    startDate?: string;
    endDate?: string;
    twoManSite?: string;
    exportType?: 'csv' | 'pdf';
    includeAllBookings?: boolean;
}
export interface HourlyRateModalProps {
    onCancel: () => void;
    onExport: (params: ExportParams) => void;
    // The export is generated by a queued job the frontend polls for, so the
    // modal stays open reporting progress until it lands.
    loading?: boolean;
}

const HourlyRateModal: FC<HourlyRateModalProps> = ({ onCancel, onExport, loading = false }) => {
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [includeAllBookings, setIncludeAllBookings] = useState<boolean>(false);

    const handleStartDateChange = (date: Dayjs | null) => {
        setStartDate(date);
    };

    const handleEndDateChange = (date: Dayjs | null) => {
        setEndDate(date);
    };

    const resetForm = () => {
        setStartDate(null);
        setEndDate(null);
        setIncludeAllBookings(false);
    };

    const handleExportClick = () => {
        if (startDate && endDate && endDate.isBefore(startDate)) {
            notification.error({
                message: 'Invalid Date Range',
                description: 'End date must be greater than or equal to start date.',
            });
            return;
        }

        const params: ExportParams = {};
        if (startDate) {
            params.startDate = startDate.format('YYYY-MM-DD');
        }
        if (endDate) {
            params.endDate = endDate.format('YYYY-MM-DD');
        }
        if (includeAllBookings) {
            params.includeAllBookings = true;
        }
        // The form is left as chosen while the export runs; the dashboard closes
        // this modal once the file lands, which resets it for next time.
        onExport(params);
    };

    const handleCancelClick = () => {
        resetForm();
        onCancel();
    };

    return (
        <div className="hourly-rate-modal-container">
            <h2 className="heading">Export Completed Visits</h2>
            <h5 className="sub-heading">Select an optional date range to export completed visits. The hourly rate is configured per site in admin.</h5>
            <div className="content-container">
                <h3>Date Range (Optional)</h3>
                <p className="date-note">If no date range is selected, all completed visits will be exported.</p>
                <div className="date-range-container">
                    <div className="date-input">
                        <label>Start Date</label>
                        <DatePicker
                            value={startDate}
                            onChange={handleStartDateChange}
                            placeholder="Select start date"
                            format="DD/MM/YYYY"
                            style={{ width: '100%' }} />
                    </div>
                    <div className="date-input">
                        <label>End Date</label>
                        <DatePicker
                            value={endDate}
                            onChange={handleEndDateChange}
                            placeholder="Select end date"
                            format="DD/MM/YYYY"
                            style={{ width: '100%' }} />
                    </div>
                </div>
                <div className="include-all-container">
                    <Checkbox
                        checked={includeAllBookings}
                        disabled={loading}
                        onClick={() => setIncludeAllBookings(!includeAllBookings)} />
                    <span className="label">Include all bookings</span>
                </div>
                {loading && (
                    <p className="export-progress">
                        Preparing your export. This can take a couple of minutes for a large date range.
                    </p>
                )}
                <div className="button-wrapper">
                    <button
                        onClick={handleExportClick}
                        type="button"
                        className="export-button"
                        disabled={loading}>
                        {loading ? 'Exporting...' : 'Export Data'}
                    </button>
                    <button onClick={handleCancelClick} type="button" className="cancel-button">
                        {loading ? 'Close' : 'Cancel'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HourlyRateModal;
